import { useTranslation } from 'react-i18next';
import { Controller, type Control, type FieldErrors, type FieldValues, type Path } from 'react-hook-form';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { HONORIFIC_VALUES } from '../../../generated/config-enums';

// The person-title value set is single-sourced in shared/config/honorific.config.json
// (WI 5313b820). It is the SAME nine values behind `users.title`,
// `people_employees.honorific` and a contact's title — this shared picker serves
// all three — so it reads the generated constant instead of re-listing them.
// Labels live on `common:honorifics.*` — one set for all three entities, since
// this one picker renders them everywhere (WI 11e9f400 folded away the divergent
// `user_management:userTitles.*` / `people:honorific.*` duplicates).

interface PersonNameFieldsProps<T extends FieldValues> {
  control: Control<T>;
  errors: FieldErrors<T>;
  /** Show title, middle_name, suffix fields. Default: true */
  showExtended?: boolean;
  /** autoFocus on first name field. Default: false */
  autoFocus?: boolean;
  /**
   * Name requirement policy:
   *  - 'both' (default): both first and last name are required, each with `*`
   *  - 'atLeastOne': at least one of first/last is required; no `*`, cross-field validate
   */
  nameRequirement?: 'both' | 'atLeastOne';
  /** Field name mapping - defaults to standard names */
  fieldNames?: {
    title?: Path<T>;
    first_name?: Path<T>;
    middle_name?: Path<T>;
    last_name?: Path<T>;
    suffix?: Path<T>;
  };
}

/**
 * Shared person name fields layout.
 * Extended (default): Row 1: Title + First Name | Row 2: Middle + Last + Suffix
 * Simple: Row 1: First Name + Last Name
 */
export function PersonNameFields<T extends FieldValues>({
  control,
  errors,
  showExtended = true,
  autoFocus = false,
  nameRequirement = 'both',
  fieldNames,
}: PersonNameFieldsProps<T>) {
  const { t } = useTranslation(['user_management', 'common']);

  const names = {
    title: (fieldNames?.title ?? 'title') as Path<T>,
    first_name: (fieldNames?.first_name ?? 'first_name') as Path<T>,
    middle_name: (fieldNames?.middle_name ?? 'middle_name') as Path<T>,
    last_name: (fieldNames?.last_name ?? 'last_name') as Path<T>,
    suffix: (fieldNames?.suffix ?? 'suffix') as Path<T>,
  };

  const atLeastOne = nameRequirement === 'atLeastOne';
  const firstNameRules = atLeastOne
    ? {
        validate: (value: unknown, formValues: FieldValues) => {
          const other = (formValues[names.last_name as string] as string | undefined) ?? '';
          const self = (value as string | undefined) ?? '';
          return self.trim() || other.trim() ? true : t('common:validation.firstOrLastNameRequired');
        },
      }
    : { required: t('common:validation.required') };
  const lastNameRules = atLeastOne
    ? {
        validate: (value: unknown, formValues: FieldValues) => {
          const other = (formValues[names.first_name as string] as string | undefined) ?? '';
          const self = (value as string | undefined) ?? '';
          return self.trim() || other.trim() ? true : t('common:validation.firstOrLastNameRequired');
        },
      }
    : { required: t('common:validation.required') };

  const getError = (name: Path<T>) => {
    const parts = (name as string).split('.');
    let err: unknown = errors;
    for (const p of parts) {
      if (err && typeof err === 'object' && p in err) {
        err = (err as Record<string, unknown>)[p];
      } else {
        return undefined;
      }
    }
    return err as { message?: string } | undefined;
  };

  if (!showExtended) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Controller
          name={names.first_name}
          control={control}
          rules={firstNameRules}
          render={({ field }) => (
            <div className="space-y-2">
              <Label required={!atLeastOne}>{t('user_management:fields.firstName')}</Label>
              <Input
                {...field}
                autoFocus={autoFocus}
                className={getError(names.first_name) ? 'border-destructive' : ''}
              />
              {getError(names.first_name) && (
                <p className="text-sm text-destructive">{getError(names.first_name)?.message}</p>
              )}
            </div>
          )}
        />
        <Controller
          name={names.last_name}
          control={control}
          rules={lastNameRules}
          render={({ field }) => (
            <div className="space-y-2">
              <Label required={!atLeastOne}>{t('user_management:fields.lastName')}</Label>
              <Input
                {...field}
                className={getError(names.last_name) ? 'border-destructive' : ''}
              />
              {getError(names.last_name) && (
                <p className="text-sm text-destructive">{getError(names.last_name)?.message}</p>
              )}
            </div>
          )}
        />
      </div>
    );
  }

  return (
    <>
      {/* Row 1: Title (1 col) + First Name (5 cols) */}
      <div className="grid grid-cols-6 gap-4">
        <Controller
          name={names.title}
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('user_management:fields.title')}</Label>
              <Select
                value={(field.value as string) || '_none'}
                onValueChange={(v) => field.onChange(v === '_none' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{t('common:app.none')}</SelectItem>
                  {HONORIFIC_VALUES.map((title) => (
                    <SelectItem key={title} value={title}>
                      {t(`common:honorifics.${title}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        />

        <Controller
          name={names.first_name}
          control={control}
          rules={firstNameRules}
          render={({ field }) => (
            <div className="space-y-2 col-span-5">
              <Label required={!atLeastOne}>{t('user_management:fields.firstName')}</Label>
              <Input
                {...field}
                autoFocus={autoFocus}
                className={getError(names.first_name) ? 'border-destructive' : ''}
              />
              {getError(names.first_name) && (
                <p className="text-sm text-destructive">{getError(names.first_name)?.message}</p>
              )}
            </div>
          )}
        />
      </div>

      {/* Row 2: Middle Name (2 cols) + Last Name (2 cols) + Suffix (2 cols) */}
      <div className="grid grid-cols-6 gap-4">
        <Controller
          name={names.middle_name}
          control={control}
          render={({ field }) => (
            <div className="space-y-2 col-span-2">
              <Label>{t('user_management:fields.middleName')}</Label>
              <Input {...field} />
            </div>
          )}
        />

        <Controller
          name={names.last_name}
          control={control}
          rules={lastNameRules}
          render={({ field }) => (
            <div className="space-y-2 col-span-2">
              <Label required={!atLeastOne}>{t('user_management:fields.lastName')}</Label>
              <Input
                {...field}
                className={getError(names.last_name) ? 'border-destructive' : ''}
              />
              {getError(names.last_name) && (
                <p className="text-sm text-destructive">{getError(names.last_name)?.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name={names.suffix}
          control={control}
          render={({ field }) => (
            <div className="space-y-2 col-span-2">
              <Label>{t('user_management:fields.suffix')}</Label>
              <Input {...field} placeholder={t('user_management:fields.suffixPlaceholder')} />
            </div>
          )}
        />
      </div>
    </>
  );
}
